/* eslint-disable */
import { useMemo, useState } from "react";
import { Info, LogOut, Phone, RefreshCw } from "lucide-react";
import { apiFetch } from "../auth/apiClient";
import "./ApprovalPendingPage.css";

// Repo convention: images live in frontend/public and are referenced via
// PUBLIC_URL (the repo .gitignore excludes *.png globally, so a src import
// would break clean-checkout builds — public assets just 404 gracefully).
const ILLUSTRATION = `${process.env.PUBLIC_URL || ""}/approval-pending.png`;

// Full-page takeover for users whose villa membership isn't APPROVED yet
// (docs/specs/approval-pending-screen.md). Replaces the role shell entirely —
// no nav, no routes — until an approver decides.

const DAY_MS = 24 * 60 * 60 * 1000;

function daysAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / DAY_MS));
}

function remindedWithin24h(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < DAY_MS;
}

export default function ApprovalPendingPage({ membership, onLogout }) {
  // Local mirrors so a remind updates the screen without a full /me refetch —
  // the server response carries the new status + last_reminded_at.
  const [status, setStatus] = useState(membership?.approval_status || "PENDING");
  const [lastRemindedAt, setLastRemindedAt] = useState(
    membership?.last_reminded_at || null
  );
  // A rejected member's re-request flips them back to PENDING and restarts
  // the days counter (spec) — mirror that locally without a /me refetch.
  const [requestedAt, setRequestedAt] = useState(
    membership?.requested_at || null
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Sticky "you can retry tomorrow" — set after a successful remind or a 429.
  const [rateLimited, setRateLimited] = useState(() =>
    remindedWithin24h(membership?.last_reminded_at)
  );

  const contact = membership?.approval_contact || null;
  const rejectionReason = membership?.rejection_reason || null;
  const requestedDays = useMemo(() => daysAgo(requestedAt), [requestedAt]);

  const isRejected = status === "REJECTED";
  const isRevoked = status === "REVOKED";
  const canReRequest = !isRevoked; // revoked members need an admin to restore

  const remind = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await apiFetch("/api/residents/request-access/remind", {
        method: "POST",
        authRequired: true,
        body: {},
      });
      if (r.approval_status) {
        if (status === "REJECTED" && r.approval_status === "PENDING") {
          // Re-request restarted the clock server-side.
          setRequestedAt(new Date().toISOString());
        }
        setStatus(r.approval_status);
      }
      if (r.last_reminded_at) setLastRemindedAt(r.last_reminded_at);
      setRateLimited(true);
    } catch (e) {
      if (e.status === 429) {
        setRateLimited(true);
      } else {
        setError(e.message || "Could not send the reminder. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const headline = isRevoked
    ? "Access revoked"
    : isRejected
    ? "Not approved"
    : "Approval Pending";

  const body = isRevoked
    ? "Your community access has been revoked. Please contact the community admin directly."
    : isRejected
    ? "Your request was not approved. Please contact the community admin directly."
    : "Thank you for your membership request. Your request is currently under review by the committee. You will be notified once it's approved.";

  const disabled = busy || rateLimited || remindedWithin24h(lastRemindedAt);

  return (
    <div className="vsap-page">
      <div className="vsap-topbar">
        <button type="button" className="vsap-signout" onClick={onLogout}>
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="vsap-card">
        <img
          src={ILLUSTRATION}
          alt=""
          aria-hidden="true"
          className="vsap-illustration"
        />

        <h1 className="vsap-headline">{headline}</h1>
        <div className="vsap-underline" aria-hidden="true" />

        <p className="vsap-body">{body}</p>

        {(isRejected || isRevoked) && rejectionReason && (
          <blockquote className="vsap-reason">“{rejectionReason}”</blockquote>
        )}

        {!isRejected && !isRevoked && requestedDays != null && (
          <p className="vsap-days">
            Requested{" "}
            {requestedDays === 0
              ? "today"
              : `${requestedDays} day${requestedDays === 1 ? "" : "s"} ago`}
          </p>
        )}

        {contact && contact.phone && (
          <a href={`tel:+91${contact.phone}`} className="vsap-call">
            <Phone size={15} />
            {contact.kind === "owner"
              ? "Call villa owner"
              : "Call facility manager"}
            {contact.name ? ` — ${contact.name}` : ""}
          </a>
        )}

        {canReRequest && (
          <button
            type="button"
            className="vsap-remind-btn"
            onClick={remind}
            disabled={disabled}
          >
            <RefreshCw size={15} className={busy ? "vsap-spin" : ""} />
            {busy
              ? "Sending…"
              : disabled
              ? "Requested again — you can retry tomorrow"
              : "Request Approval Again"}
          </button>
        )}

        {error && <div className="vsap-error">{error}</div>}

        <div className="vsap-info">
          <Info size={15} />
          <span>
            This helps us ensure a safe and trusted community for all members.
          </span>
        </div>
      </div>
    </div>
  );
}
